import React, { useEffect, useState, useMemo, ChangeEvent, useCallback } from "react";
import { PersonalCreditInfoForm } from "src/app/views";
import { useLocationLibbyCall, useLocationDAO } from "src/app/business";
import { State, City } from "src/app/models";
import { useDebounce } from "src/commons";
import { orderBy } from "lodash";
import { PROVINCES } from "src/app/const";
import { useCheckoutFormContext, useCreditAddressLogic } from "src/screens/Public/Checkout/hooks";
import { PaymentInitialValue, docentInitialValue, DocentInitialValue } from "../../initialValues";
import { ValuesForm, FormProps } from "../../types";

interface Values extends ValuesForm {
  docentInfo: DocentInitialValue<Partial<File>>;
  paymentMethod: PaymentInitialValue;
}

export const DocenteInfoForm = ({ onBackStep }: FormProps) => {
  const { form, values, errors, handleSubmit } = useCheckoutFormContext();
  const [cities, setCities] = useState<City[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const locationDAO = useLocationDAO();
  const { data, working } = useLocationLibbyCall<State[]>({ methodName: "getStates" });
  useCreditAddressLogic("docentInfo");

  const valuesAll = useMemo(
    () =>
      ({
        docentInfo: docentInitialValue,
        ...values,
      } as Values),
    [values],
  );

  const provinces = useMemo(() => {
    let provincesFiltered: State[] = data?.filter((item) => item.id.toString() === PROVINCES.CATAMARCA.toString()) || [];
    if (valuesAll.useDeliveryAddress) {
      provincesFiltered = [...provincesFiltered, valuesAll.deliveryInfo.province];
    }
    return provincesFiltered;
  }, [data, valuesAll.useDeliveryAddress, valuesAll.deliveryInfo.province]);

  const [search, setSearch] = useState(valuesAll.docentInfo.city?.name || "");
  const searchDebunce = useDebounce(search, 600);

  useEffect(() => {
    if (valuesAll.useDeliveryAddress && search !== valuesAll.deliveryInfo.city.name) {
      setSearch(valuesAll.deliveryInfo.city.name);
    } else if (!valuesAll.useDeliveryAddress && search === valuesAll.deliveryInfo.city.name) {
      setSearch("");
    }
  }, [valuesAll.useDeliveryAddress, valuesAll.deliveryInfo.city.name, search]);

  const selectedProvince = useMemo(() => valuesAll.docentInfo.province?.id !== "", [valuesAll.docentInfo.province?.id]);

  const zitCode = useMemo(() => valuesAll.docentInfo.zipCode || "", [valuesAll.docentInfo.zipCode]);

  const onInputChangeCity = useCallback(
    (event: ChangeEvent<HTMLInputElement>, value: string) => {
      setSearch(value);
    },
    [setSearch],
  );

  useEffect(() => {
    if ((valuesAll?.docentInfo?.city?.name?.length > 0 && valuesAll?.docentInfo?.city?.name !== search) || valuesAll?.docentInfo?.city === null) {
      const copyValuesForm = { ...valuesAll };
      copyValuesForm.docentInfo.city = docentInitialValue.city;
      form.reset(copyValuesForm);
    }
  }, [search, form, valuesAll]);

  useEffect(() => {
    if (selectedProvince && searchDebunce && searchDebunce !== valuesAll.docentInfo.city?.name) {
      setLoading(true);
      (async () => {
        const city = await locationDAO.getCityByState(valuesAll.docentInfo.province?.id, searchDebunce);
        setCities(city);
        setLoading(false);
      })();
    }
  }, [selectedProvince, locationDAO, valuesAll.docentInfo.province, searchDebunce, valuesAll.docentInfo.city]);

  const invalidZipCode = useMemo(() => !!zitCode && !valuesAll.docentInfo.city?.zip?.includes(Number(zitCode)), [valuesAll.docentInfo.city, zitCode]);

  return (
    <PersonalCreditInfoForm
      title="Datos del solicitante"
      nameForm="docentInfo"
      nameDelivery="docentInfo"
      loading={working}
      invalidZidCode={invalidZipCode}
      loadingCities={loading}
      disableNext={Object.keys(errors as any).length !== 0}
      onClickStep={handleSubmit}
      onBackStep={onBackStep}
      form={form}
      provinces={orderBy(provinces, "name")}
      selectedProvince={selectedProvince}
      cities={cities}
      onInputChangeCity={onInputChangeCity}
      zipCodeLength={zitCode.length || 0}
      searchCityValue={search}
      useSameDeliveryAddress={values.useDeliveryAddress}
      showCheckbox
    />
  );
};
